import React, { useState, ReactNode } from 'react';
import { User } from "firebase/auth";
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  AppBar, Toolbar, IconButton, Typography, Box, Menu, MenuItem, Chip, Stack,
  ListItemIcon, Divider, Avatar, Collapse, List, ListItemButton,
  ListItemText, Drawer, FormControlLabel, Switch, FormControl, InputLabel, Select,
  useTheme, useMediaQuery
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import EventIcon from '@mui/icons-material/Event';
import Logout from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import TimelineIcon from '@mui/icons-material/Timeline';
import PersonIcon from '@mui/icons-material/Person';
import LinkIcon from '@mui/icons-material/Link';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports'; // Games Icon
import ContactsIcon from '@mui/icons-material/Contacts';
import ExpandLess from '@mui/icons-material/ExpandLess';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import VideocamIcon from '@mui/icons-material/Videocam';
import { logEvent } from '../analytics';
import { getBackendEnv, setBackendEnv } from '../config';
import { SEASONS } from '../seasons';
import { getTournamentsForSeason } from '../tournaments';
import { useSeason } from '../SeasonContext';
import { useRoster } from '../RosterContext';
import { MultiAuthUser } from '../hooks/useMultiAuth';

export const SHOP_LINKS_BY_SEASON: Record<string, { label: string; url: string }[]> = {
  'pw_26_27': [
    { label: 'Jets Spiritwear', url: 'https://www.youth.inc/shop/myteam/chicago-il-chicago-jets-hockey-club?logo=chicago-il-chicago-jets-hockey-club-baby-blue-jet' },
    { label: 'Uniform Store', url: 'https://chcj2026.itemorder.com/shop/home/' }
  ],
  'pw_25_26': [
    { label: 'Jets Spiritwear Store', url: 'https://chicagojetstest.itemorder.com/shop/home/' },
    { label: 'dotdotdot', url: 'https://www.dotdotdotbykook.com/' }
  ]
};

export interface MainLayoutProps {
  children: ReactNode;
  user: User | null;
  allUsers?: MultiAuthUser[];
  onSwitchUser?: (appId: string) => void;
  onAddAccount?: () => void;
  onSignOut: () => void;
  onSignOutAll?: () => void;
  isTeamManager: boolean;
}

const MainLayout: React.FC<MainLayoutProps> = ({ 
  children, 
  user, 
  allUsers = [], 
  onSwitchUser, 
  onAddAccount, 
  onSignOut, 
  onSignOutAll, 
  isTeamManager 
}) => {
  const navigate = useNavigate();
  const { selectedSeason, setSelectedSeasonKey, isViewingPastSeason } = useSeason();
  const rosterContext = useRoster();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tournamentsOpen, setTournamentsOpen] = useState(true);
  const [trendsOpen, setTrendsOpen] = useState(true);
  const [shopOpen, setShopOpen] = useState(true);
  const [gamesOpen, setGamesOpen] = useState(true);
  
  const backendEnv = getBackendEnv();
  const open = Boolean(anchorEl);
  const userEmail = (user?.email || rosterContext.userProfile?.email || '').trim().toLowerCase();
  const isSuperAdmin = userEmail === 'bwuest@gmail.com';
  const isAdmin = isSuperAdmin || Boolean(rosterContext.isAdmin || rosterContext.userProfile?.isAdmin);
  const canAccessHighlights = Boolean(user || userEmail);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const isStudioPage = /^\/(highlights|videos)\/[^/]+/.test(location.pathname);
  const hostname = window.location.hostname;
  // Show dev toggle for admins or on specific development/preview hostnames.
  const showDevToggle = isAdmin || hostname.includes('localhost') || hostname.includes('super-carnival');

  const tournaments = getTournamentsForSeason(selectedSeason.key);
  const shopLinks = SHOP_LINKS_BY_SEASON[selectedSeason.key] || [];

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setAnchorEl(null);
  };

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleNavigate = (path: string, eventName: string, eventParams?: Record<string, any>) => (e: React.MouseEvent) => {
    if (eventParams) {
      logEvent(eventName, eventParams);
    }
    setDrawerOpen(false);
    navigate(path);
  };
  
  const handleSignOut = () => {
    onSignOut();
    handleProfileClose();
  };

  const handleSignOutAll = () => {
    if (onSignOutAll) {
      onSignOutAll();
    } else {
      onSignOut();
    }
    handleProfileClose();
  };

  const handleBackendChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEnv = event.target.checked ? 'dev' : 'prod';
    logEvent('toggle_backend_env', { env: newEnv });
    setBackendEnv(newEnv); // This will save to localStorage and reload the page
  };

  const hasCalendar = selectedSeason.key === 'pw_26_27';
  const hasVolunteer = !isViewingPastSeason;
  const hasRoster = selectedSeason.key === 'pw_26_27' || selectedSeason.key === 'pw_25_26';
  const hasTournaments = tournaments.length > 0;
  const hasShop = shopLinks.length > 0;
  const hasLinks = selectedSeason.key === 'pw_26_27' || selectedSeason.key === 'pw_25_26';

  const drawerList = (
    <Box
      sx={{ width: 260 }}
      role="presentation"
    >
      <Toolbar />
      {/* Season Selector Dropdown */}
      <Box sx={{ p: 2, pb: 1 }}>
        <FormControl fullWidth size="small">
          <InputLabel id="season-select-label">Season</InputLabel>
          <Select
            labelId="season-select-label"
            id="season-select"
            value={selectedSeason.key}
            label="Season"
            onChange={(e) => {
              const newKey = e.target.value;
              setSelectedSeasonKey(newKey);
              logEvent('select_season', { season_key: newKey });
            }}
          >
            {SEASONS.map((season) => (
              <MenuItem key={season.key} value={season.key}>
                {season.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Divider />
      <List>
        <ListItemButton onClick={handleNavigate('/', 'navigate', { to: 'Home', from: 'drawer' })}>
          <ListItemIcon><HomeIcon /></ListItemIcon>
          <ListItemText primary="Home" />
        </ListItemButton>

        {hasCalendar && (
          <ListItemButton onClick={handleNavigate('/calendar', 'navigate', { to: 'Calendar', from: 'drawer' })}>
            <ListItemIcon><EventIcon /></ListItemIcon>
            <ListItemText primary="Calendar" />
          </ListItemButton>
        )}

        {canAccessHighlights && (
          <ListItemButton onClick={handleNavigate('/highlights', 'navigate', { to: 'Game Highlights', from: 'drawer' })}>
            <ListItemIcon><VideocamIcon /></ListItemIcon>
            <ListItemText primary="Game Highlights" />
          </ListItemButton>
        )}

        {hasVolunteer && (
          <ListItemButton onClick={handleNavigate('/volunteers', 'navigate', { to: 'Volunteers', from: 'drawer' })}>
            <ListItemIcon><VolunteerActivismIcon /></ListItemIcon>
            <ListItemText primary="Volunteers" />
          </ListItemButton>
        )}

        {hasRoster && (
          <ListItemButton onClick={handleNavigate('/roster', 'navigate', { to: 'Roster', from: 'drawer' })}>
            <ListItemIcon><ContactsIcon /></ListItemIcon>
            <ListItemText primary="Roster & Contacts" />
          </ListItemButton>
        )}

        <ListItemButton onClick={handleNavigate(`/standings/${selectedSeason.key}`, 'navigate', { to: `Standings - ${selectedSeason.label}`, from: 'drawer' })}>
          <ListItemIcon><LeaderboardIcon /></ListItemIcon>
          <ListItemText primary="Standings" />
        </ListItemButton>

        {hasTournaments && (
          <>
            <ListItemButton onClick={() => { setTournamentsOpen(!tournamentsOpen); logEvent('toggle_drawer_section', { section_title: 'Tournaments', open: !tournamentsOpen }); }}>
              <ListItemIcon><EmojiEventsIcon /></ListItemIcon>
              <ListItemText primary="Tournaments" />
              {tournamentsOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={tournamentsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {tournaments.map(tournament => (
                  <ListItemButton key={tournament.key} sx={{ pl: 4 }} onClick={handleNavigate(`/tournaments/${tournament.key}`, 'navigate', { to: `Tournament - ${tournament.name}`, from: 'drawer' })}>
                    {tournament.logo && (
                      <ListItemIcon sx={{ minWidth: 32, mr: 1 }}>
                        <img src={tournament.logo.startsWith('http') ? tournament.logo : process.env.PUBLIC_URL + tournament.logo} alt="" style={{ height: 20, width: 20, objectFit: 'contain' }} />
                      </ListItemIcon>
                    )}
                    <ListItemText primary={tournament.name} primaryTypographyProps={{ style: { whiteSpace: 'normal' } }} />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </>
        )}
        
        {/* Admin Only Links */}
        {isAdmin && (
            <>
                <ListItemButton onClick={handleNavigate('/admin', 'navigate', { to: 'Admin Access', from: 'drawer' })}>
                    <ListItemIcon><AdminPanelSettingsIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Admin & Access" />
                </ListItemButton>

                <ListItemButton onClick={() => { setGamesOpen(!gamesOpen); logEvent('toggle_drawer_section', { section_title: 'Games', open: !gamesOpen }); }}>
                    <ListItemIcon><SportsEsportsIcon /></ListItemIcon>
                    <ListItemText primary="Games" />
                    {gamesOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={gamesOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    <ListItemButton sx={{ pl: 4 }} onClick={handleNavigate('/puckle', 'navigate', { to: 'Puckle', from: 'drawer' })}>
                    <ListItemText primary="Puckle" />
                    </ListItemButton>
                </List>
                </Collapse>

                <ListItemButton onClick={() => { setTrendsOpen(!trendsOpen); logEvent('toggle_drawer_section', { section_title: 'Trends', open: !trendsOpen }); }}>
                    <ListItemIcon><TimelineIcon /></ListItemIcon>
                    <ListItemText primary="Trends" />
                    {trendsOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={trendsOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    <ListItemButton sx={{ pl: 4 }} onClick={handleNavigate('/trends', 'navigate', { to: 'Trends', from: 'drawer' })}>
                        <ListItemText primary="Cross-Season" />
                    </ListItemButton>
                </List>
                </Collapse>
                <ListItemButton onClick={handleNavigate('/player-stats', 'navigate', { to: 'Player Stats', from: 'drawer' })}>
                    <ListItemIcon><PersonIcon /></ListItemIcon>
                    <ListItemText primary="Player Stats" />
                </ListItemButton>
            </>
        )}

        {hasLinks && (
          <ListItemButton onClick={handleNavigate('/links', 'navigate', { to: 'Links', from: 'drawer' })}>
            <ListItemIcon><LinkIcon /></ListItemIcon>
            <ListItemText primary="Links" />
          </ListItemButton>
        )}

        {hasShop && (
          <>
            <ListItemButton onClick={() => { setShopOpen(!shopOpen); logEvent('toggle_drawer_section', { section_title: 'Shop', open: !shopOpen }); }}>
              <ListItemIcon><StorefrontIcon /></ListItemIcon>
              <ListItemText primary="Shop" />
              {shopOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={shopOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {shopLinks.map(shopItem => (
                  <ListItemButton
                    key={shopItem.label}
                    sx={{ pl: 4 }}
                    component="a"
                    href={shopItem.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => { logEvent('navigate_external', { to: shopItem.label, from: 'drawer' }); toggleDrawer(false)(e); }}
                  >
                    <ListItemText primary={shopItem.label} />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <>
      {!isStudioPage && (
        <AppBar 
          position="static" 
          elevation={0} 
          sx={{ 
            backgroundColor: 'background.paper', 
            borderBottom: 1, 
            borderColor: 'divider' 
          }}
        >
          <Toolbar>
            <IconButton
              size="large"
              edge="start"
              aria-label="menu"
              sx={{ mr: 2, color: 'text.primary' }}
              onClick={(e) => {
                  toggleDrawer(true)(e);
                  logEvent('open_drawer_menu');
              }}
            >
              <MenuIcon />
            </IconButton>
            <img 
              src={process.env.PUBLIC_URL + '/chicago_jets_shield_logo.jpg'} 
              alt="Chicago Jets Logo" 
              style={{ height: 40, marginRight: '16px' }} 
            />
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'text.primary' }}>
              Chicago Jets 2014
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 2 }}>
              {isViewingPastSeason && (
                <Chip 
                  label={selectedSeason.chipLabel || selectedSeason.label} 
                  color="warning" 
                  size="small" 
                  sx={{ fontWeight: 'bold' }} 
                />
              )}
              {isTeamManager && (
                  <Chip label="Team Manager" color="info" size="small" />
              )}
              {backendEnv === 'dev' && (
                <Chip label="Dev Environment" color="warning" size="small" />
              )}
              <IconButton
                onClick={(e) => {
                    handleProfileClick(e);
                    logEvent('open_profile_menu');
                }}
                size="small"
                aria-controls={open ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
              >
                <Avatar sx={{ width: 32, height: 32 }} src={user?.photoURL ?? undefined}>
                  {!user?.photoURL && user?.email ? user.email.charAt(0).toUpperCase() : ''}
                </Avatar>
              </IconButton>
            </Stack>
          </Toolbar>
        </AppBar>
      )}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
      >
        {drawerList}
      </Drawer>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleProfileClose}
        PaperProps={{
          elevation: 3,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
            mt: 1.5,
            minWidth: 260,
            borderRadius: 2,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {allUsers && allUsers.length > 0 ? (
          <Box sx={{ py: 0.5 }}>
            <Typography variant="caption" sx={{ px: 2, py: 0.5, color: 'text.secondary', fontWeight: 600, display: 'block', textTransform: 'uppercase' }}>
              Accounts
            </Typography>
            {allUsers.map((mu) => {
              const isActive = mu.user.uid === user?.uid;
              return (
                <MenuItem 
                  key={mu.appId} 
                  onClick={() => {
                    if (onSwitchUser) onSwitchUser(mu.appId);
                    handleProfileClose();
                  }}
                  sx={{ 
                    py: 1, 
                    px: 2, 
                    bgcolor: isActive ? 'action.selected' : 'inherit',
                    gap: 1.5
                  }}
                >
                  <Avatar 
                    src={mu.user.photoURL || undefined} 
                    sx={{ width: 32, height: 32, bgcolor: isActive ? 'primary.main' : 'grey.400', fontSize: '0.9rem' }}
                  >
                    {!mu.user.photoURL && mu.user.email ? mu.user.email.charAt(0).toUpperCase() : ''}
                  </Avatar>
                  <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: isActive ? 600 : 400 }} noWrap>
                      {mu.user.displayName || mu.user.email?.split('@')[0]}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                      {mu.user.email}
                    </Typography>
                  </Box>
                  {isActive && <CheckIcon fontSize="small" color="primary" />}
                </MenuItem>
              );
            })}
            <Divider sx={{ my: 1 }} />
          </Box>
        ) : (
          <Box sx={{ p: 2, pt: 1, pb: 1 }}>
            <Typography variant="body1" fontWeight="bold">{user?.displayName || user?.email?.split('@')[0]}</Typography>
            <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
          </Box>
        )}

        {isAdmin && (
          <MenuItem onClick={() => { handleProfileClose(); navigate('/admin'); }}>
            <ListItemIcon>
              <AdminPanelSettingsIcon fontSize="small" color="primary" />
            </ListItemIcon>
            Admin & Role Management
          </MenuItem>
        )}

        <MenuItem onClick={() => { handleProfileClose(); navigate('/settings'); }}>
          <ListItemIcon>
            <ManageAccountsIcon fontSize="small" />
          </ListItemIcon>
          Settings &amp; Preferences
        </MenuItem>

        {onAddAccount && (
          <MenuItem onClick={() => { onAddAccount(); handleProfileClose(); }}>
            <ListItemIcon>
              <AddIcon fontSize="small" />
            </ListItemIcon>
            Add another account
          </MenuItem>
        )}

        {showDevToggle && <Divider />}
        {showDevToggle && (
          <MenuItem onClick={(e) => e.stopPropagation()} sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
            <FormControlLabel
              control={<Switch checked={backendEnv === 'dev'} onChange={handleBackendChange} size="small" />}
              label="Dev Backend"
              labelPlacement="start"
              sx={{ m: 0, p: '6px 8px', width: '100%', justifyContent: 'space-between' }}
            />
          </MenuItem>
        )}

        <Divider />

        {allUsers && allUsers.length > 1 ? [
          <MenuItem key="signout-one" onClick={handleSignOut}>
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            Sign out of this account
          </MenuItem>,
          <MenuItem key="signout-all" onClick={handleSignOutAll} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <ExitToAppIcon fontSize="small" color="error" />
            </ListItemIcon>
            Sign out of all accounts
          </MenuItem>
        ] : (
          <MenuItem onClick={handleSignOut}>
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            Sign Out
          </MenuItem>
        )}
      </Menu>

      <main
        style={
          isStudioPage
            ? {
                height: isMobile ? 'auto' : '100vh',
                minHeight: isMobile ? '100dvh' : undefined,
                width: '100vw',
                overflow: isMobile ? 'visible' : 'hidden',
                backgroundColor: '#000',
              }
            : undefined
        }
      >
        {children}
      </main>
    </>
  );
};

export default MainLayout;
